import { Alert, Table, TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'translations/hooks';

import { useTableParams } from 'business/providers/table-params';
import { PAGE_OPTIONS } from 'business/quality-control/constants';
import { useGetListData } from 'business/quality-control/pages/ring-compliance-report/hooks/data/use-get-list';
import { useRingComplianceReportColumns } from 'business/quality-control/pages/ring-compliance-report/hooks/use-columns';
import { RingComplianceReportFilterData } from 'business/quality-control/pages/ring-compliance-report/types';
import Routes from 'config/routes';
import { FilterDataSetter } from 'technical/filter-data';

export type RingComplianceReport = {
  id: string;
  ringNumber: number;
  complianceDate: string | null;
  approvalDate: string | null;
  refusalDate: string | null;
};

export const RingComplianceTable = ({
  setFilterData,
  mobile = false,
}: {
  setFilterData: FilterDataSetter<RingComplianceReportFilterData>;
  mobile?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { tableParams, setTableParams, defaultTableParams } = useTableParams();

  const { data, total, isLoading, error } = useGetListData(setFilterData);

  const onExpand = (id: string) =>
    navigate(
      generatePath(Routes.QualityRingComplianceReportDetail, { ringId: id }),
      { state: { backTo: `${location.pathname}${location.search}` } },
    );

  const columns = useRingComplianceReportColumns(onExpand, mobile);

  const handleChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter:
      | SorterResult<RingComplianceReport>
      | SorterResult<RingComplianceReport>[],
  ) => {
    let sortByColumn;
    if (Array.isArray(sorter)) {
      sortByColumn = sorter[0];
    } else {
      sortByColumn = sorter;
    }

    setTableParams({
      current: pagination.current ?? defaultTableParams.current,
      pageSize: pagination.pageSize ?? defaultTableParams.pageSize,
      sortOrder: sortByColumn.order ?? defaultTableParams.sortOrder,
      sortField: sortByColumn.field ?? defaultTableParams.sortField,
    });
  };

  if (error) {
    return <Alert message={t('errors.error_generic')} type="error" />;
  }

  return (
    <>
      <Table
        bordered
        scroll={{ x: 'max-content' }}
        // 90 is the value of src/business/App.scss - $header-height
        sticky={{ offsetHeader: mobile ? 0 : 90 }}
        size="small"
        dataSource={data}
        columns={columns}
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_OPTIONS,
        }}
        onChange={handleChange}
        loading={isLoading}
      />
    </>
  );
};
